<script lang="ts">
	import {
		Alert,
		AlertV2,
		ButtonV2,
		Container,
		Loader,
		MenuItemList,
		Modal,
		Stack,
		Text,
		toast,
	} from '@Shelly/shelly-design-system';
	import {
		checkUnitDevices,
		deleteUnit,
		transferUnit,
		transferUnitToNewAccount,
	} from 'src/services/iotu/house-management';
	import { switchUnit } from 'src/services/auth_v2';
	import { currentUnitId, homes, refreshHomes } from 'src/stores/account/home';
	import { get } from 'svelte/store';
	import { user_data, user_settings } from 'src/stores/account/user';
	import { isSubUnit } from 'src/helpers/user_helpers';
	import HomesOnboardingWizard from './HomesOnboardingWizard.svelte';
	import { migrateIcodesOnUnitChange } from 'src/helpers/installer_helpers';
	import { profiles } from 'src/stores/features/profiles';
	import type { DropdownMenuDataType } from 'src/components/Profiles/DropdownItem.types';
	import { format } from 'svelte-i18n';
	import { MAX_OWNED_HOMES } from 'src/constants';
	import UnitItem from './UnitItem.svelte';
	import UnitDeleteCheckModal from './UnitDeleteCheckModal.svelte';
	import AddHomeWizardModal from './shared/AddHomeWizardModal.svelte';
	import EditHomeModal from 'src/components/Homes/EditHomeModal.svelte';
	import {
		getFallbackUnitIdForDelete,
		normalizeCheckResponse,
		type UnitDeviceCheck,
	} from './shared/unit-deletion-flow';
	import {
		canAddOwnedHome,
		findOwnedHome,
		getProfileDisplayName,
	} from 'src/helpers/houses/homes_helpers';

	export let excludeCurrentHome = false;

	let showHomesOnboarding = false;
	let isLoading = false;

	let showAddHomeWizard = false;

	let showEditHomeModal = false;
	let editHomeId: string | null = null;

	let showCheckModal = false;
	let unitToDelete: { id: string; name: string } | null = null;
	let deviceCheck: UnitDeviceCheck | null = null;
	let originalUnitId: string | null = null;

	let showTransferModal = false;
	let unitToTransfer: { id: string; name: string } | null = null;
	let selectedTransferProfile: DropdownMenuDataType | null = null;
	let showTransferNewAccountConfirm = false;
	let showTransferAccountDeletionWarning = false;

	$: units = $user_data.available_units ?? {};
	$: allUnitEntries = Object.entries(units) as [string, string][];
	$: unitEntries = excludeCurrentHome
		? allUnitEntries.filter(([unitId]) => unitId !== $currentUnitId)
		: allUnitEntries;
	$: ownedHomesCount = allUnitEntries.length;
	$: otherProfiles = $profiles.filter((p) => !p.isSelected);
	$: isLastUnit = allUnitEntries.length <= 1;
	$: hasOwnedHomes = Object.keys(units).length > 0;
	$: homeToEdit =
		editHomeId == null ? undefined : findOwnedHome($homes.list, editHomeId, units, $currentUnitId);
	$: if (!showEditHomeModal) {
		editHomeId = null;
	}

	const applyIcodesMigration = (previousUnitCount: number): void => {
		const currentUnitCount = Object.keys($user_data.available_units ?? {}).length;
		const nextIcodes = migrateIcodesOnUnitChange(
			$user_settings?.icodes,
			String($user_data?.user_id),
			previousUnitCount,
			currentUnitCount,
		);
		if (nextIcodes !== null) {
			user_settings.set({ ...$user_settings, icodes: nextIcodes });
		}
	};

	function openAddHomeWizard(): void {
		if (!canAddOwnedHome(units)) return;
		showAddHomeWizard = true;
	}

	function onEditUnit(e: CustomEvent<{ unitId: string }>): void {
		editHomeId = e.detail.unitId;
		showEditHomeModal = true;
	}

	async function runDeviceCheckForUnit(id: string, name: string): Promise<void> {
		originalUnitId = $currentUnitId;
		unitToDelete = { id, name };
		deviceCheck = null;
		isLoading = true;
		try {
			const res = await checkUnitDevices(Number(id));
			if (!res.isok) {
				throw new Error('check failed');
			}
			deviceCheck = normalizeCheckResponse(res);
			showCheckModal = true;
		} catch {
			toast.open({
				type: 'danger',
				title: $format('error_check_unit_devices', {
					default: 'Could not check this home. Please try again.',
				}),
			});
			unitToDelete = null;
			originalUnitId = null;
		} finally {
			isLoading = false;
		}
	}

	async function onDeleteUnit(e: CustomEvent<{ unitId: string }>): Promise<void> {
		const id = e.detail.unitId;
		await runDeviceCheckForUnit(id, units[id] ?? id);
	}

	function handleCheckCancel(): void {
		showCheckModal = false;
		unitToDelete = null;
		deviceCheck = null;
		originalUnitId = null;
	}

	async function handleCheckSwitchToHome(): Promise<void> {
		if (!unitToDelete) return;
		const targetId = unitToDelete.id;
		showCheckModal = false;
		deviceCheck = null;
		isLoading = true;
		try {
			await switchUnit(targetId);
		} catch {
			toast.open({
				type: 'danger',
				title: $format('error_switch_unit_failed', {
					default: 'Could not switch to this home. Please try again.',
				}),
			});
		} finally {
			isLoading = false;
			unitToDelete = null;
			originalUnitId = null;
		}
	}

	async function performDeleteUnitById(unitIdStr: string): Promise<void> {
		const prevCount = allUnitEntries.length;
		const result = await deleteUnit(Number(unitIdStr));
		if (!result.isok) {
			throw new Error($format('error_delete_unit', { default: 'Failed to delete home' }));
		}
		await refreshHomes();
		applyIcodesMigration(prevCount);
		toast.open({
			type: 'success',
			title: $format('success_delete_unit', { default: 'Home deleted successfully' }),
		});
	}

	async function handleCheckDelete(): Promise<void> {
		if (!unitToDelete) return;
		const targetId = unitToDelete.id;
		showCheckModal = false;
		deviceCheck = null;
		isLoading = true;
		try {
			if (get(currentUnitId) === targetId) {
				const availableUnitIds = Object.keys(get(user_data).available_units ?? {});
				const fallbackId = getFallbackUnitIdForDelete({
					targetUnitId: targetId,
					originalUnitId,
					availableUnitIds,
				});
				if (!fallbackId) {
					throw new Error(
						$format('error_delete_unit_no_fallback', {
							default:
								'No other home is available to switch to. You cannot delete the home you are currently using.',
						}),
					);
				}
				await switchUnit(fallbackId);
			}
			await performDeleteUnitById(targetId);
		} catch (err: any) {
			toast.open({
				type: 'danger',
				title:
					err?.message ?? $format('error_delete_unit', { default: 'Failed to delete home' }),
			});
		} finally {
			isLoading = false;
			originalUnitId = null;
			unitToDelete = null;
		}
	}

	function onTransferUnit(e: CustomEvent<{ unitId: string }>): void {
		const id = e.detail.unitId;
		unitToTransfer = { id, name: units[id] ?? id };
		selectedTransferProfile = null;
		showTransferModal = true;
	}

	function handleTransferSubmit(): void {
		if (!selectedTransferProfile) return;

		showTransferModal = false;

		if (isLastUnit) {
			showTransferAccountDeletionWarning = true;
		} else {
			executeTransfer();
		}
	}

	function handleTransferAccountDeletionConfirmed(): void {
		showTransferAccountDeletionWarning = false;
		executeTransfer();
	}

	function handleTransferNewAccountDismissed(): void {
		showTransferNewAccountConfirm = false;
		toast.open({
			type: 'success',
			title: $format('success_transfer_unit', {
				default: 'Home transferred successfully',
			}),
		});
		unitToTransfer = null;
		selectedTransferProfile = null;
	}

	async function executeTransfer(): Promise<void> {
		if (!unitToTransfer || !selectedTransferProfile) return;

		isLoading = true;
		const prevCount = allUnitEntries.length;
		const uid = Number(unitToTransfer.id);
		const email = selectedTransferProfile.email ?? '';
		const pass = selectedTransferProfile.pass ?? '';

		try {
			let transferSucceeded = false;

			try {
				const result = await transferUnit(uid, email, pass);
				transferSucceeded = result.isok;
			} catch {
				transferSucceeded = false;
			}

			if (!transferSucceeded) {
				const fallback = await transferUnitToNewAccount(uid, email, pass);
				if (!fallback.isok) {
					throw new Error(
						$format('error_transfer_unit', { default: 'Failed to transfer home' }),
					);
				}
			}

			await refreshHomes();
			applyIcodesMigration(prevCount);

			if (!transferSucceeded) {
				showTransferNewAccountConfirm = true;
			} else {
				toast.open({
					type: 'success',
					title: $format('success_transfer_unit', {
						default: 'Home transferred successfully',
					}),
				});
				unitToTransfer = null;
				selectedTransferProfile = null;
			}
		} catch (err: any) {
			toast.open({
				type: 'danger',
				title:
					err?.message ??
					$format('error_transfer_unit', { default: 'Failed to transfer home' }),
			});
			unitToTransfer = null;
			selectedTransferProfile = null;
		} finally {
			isLoading = false;
		}
	}

	function onCreateOwnHome(): void {
		if (!canAddOwnedHome($user_data.available_units)) {
			toast.open({
				type: 'warning',
				title: $format('alert_owned_homes_limit_reached', {
					values: { limit: MAX_OWNED_HOMES },
					default: `You can create up to ${MAX_OWNED_HOMES} homes. Delete an existing home to create a new one.`,
				}),
				duration: 4000,
			});
			return;
		}

		showAddHomeWizard = true;
	}
</script>

{#if !isSubUnit()}
	<Stack direction="column" gap="s" class="mb-m">
		<AlertV2
			heading={$format('homes_onboarding_alert_heading', {
				default: 'Get to know Home Management',
			})}
			message={$format('homes_onboarding_alert_message', {
				default:
					'Learn how to manage multiple homes, transfer ownership, receive homes, and share access with family or guests.',
			})}
		>
			<ButtonV2
				slot="bottom"
				variant="secondary"
				size="m"
				on:click={() => (showHomesOnboarding = true)}
				aria-label={$format('link_learn_more', { default: 'Learn more' })}
			>
				{$format('link_learn_more', { default: 'Learn more' })}
			</ButtonV2>
		</AlertV2>
	</Stack>
{/if}

<HomesOnboardingWizard bind:showModal={showHomesOnboarding} />

{#if !hasOwnedHomes}
	<div class="d-flex flex-column gap-s mt-m justify-content-between align-items-center">
		<Text styleType="body" class="mb-xs">
			{$format('shared_create_own_home_section_title', { default: 'Your Homes' })}
		</Text>
		<ButtonV2
			variant="secondary"
			size="m"
			icon="fas fa-plus"
			loading={isLoading}
			on:click={onCreateOwnHome}
			aria-label={$format('shared_create_own_home_button', {
				default: 'Create your own home',
			})}
		>
			{$format('shared_create_own_home_button', { default: 'Create your own home' })}
		</ButtonV2>
	</div>
	<Alert
		type="info"
		class="mt-s"
		heading={$format('shared_create_own_home_section_info', {
			default: "You don't have your own home yet. Create one to add your own devices.",
		})}
	/>
{:else}
	<Stack
		direction="column"
		directionMobile="row"
		align="between"
		alignMobile="none"
		gap="m"
		class="mt-m mb-s align-items-center"
	>
		<Text styleType="body">
			{$format('settings_unit_management_title', { default: 'Homes' })}
		</Text>
		<Stack direction="column" directionMobile="row" gap="xs">
			<ButtonV2
				icon="fas fa-plus"
				variant="secondary"
				size="m"
				loading={isLoading}
				disabled={!canAddOwnedHome(units)}
				on:click={openAddHomeWizard}
			>
				{$format('button_add_home', { default: 'Add home' })}
			</ButtonV2>
		</Stack>
	</Stack>
{/if}

{#if !canAddOwnedHome(units)}
	<Alert
		type="warning"
		class="mb-s"
		heading={$format('alert_owned_homes_limit_reached', {
			values: { limit: MAX_OWNED_HOMES },
			default: `You can create up to ${MAX_OWNED_HOMES} homes. Delete an existing home to add a new one.`,
		})}
	/>
{:else if ownedHomesCount > 0}
	<Text styleType="body-small" class="mb-s">
		{$format('alert_owned_homes_count', {
			values: { count: ownedHomesCount, limit: MAX_OWNED_HOMES },
			default: `Homes owned: ${ownedHomesCount}/${MAX_OWNED_HOMES}`,
		})}
	</Text>
{/if}

{#if isLoading}
	<div class="d-flex justify-content-center">
		<Loader size="m" />
	</div>
{/if}
{#each unitEntries as [unitId, unitName] (unitId)}
	{@const home = $homes.list.find((entry) => entry.id === unitId && !entry.isShared)}
	<div class="mb-s">
		<UnitItem
			{unitId}
			unitName={home?.name ?? unitName}
			{home}
			isCurrent={unitId === $currentUnitId}
			on:edit={onEditUnit}
			on:delete={onDeleteUnit}
			on:transfer={onTransferUnit}
		/>
	</div>
{/each}

<EditHomeModal bind:showModal={showEditHomeModal} home={homeToEdit} />

<AddHomeWizardModal bind:showModal={showAddHomeWizard} variant="v2" />

<UnitDeleteCheckModal
	showModal={showCheckModal}
	unitName={unitToDelete?.name ?? ''}
	ownedCount={deviceCheck?.ownedCount ?? 0}
	sharedCount={deviceCheck?.sharedCount ?? 0}
	useV2={true}
	on:cancel={handleCheckCancel}
	on:switchToHome={handleCheckSwitchToHome}
	on:delete={handleCheckDelete}
/>

<Modal
	showModal={showTransferModal}
	on:close={() => (showTransferModal = false)}
	title={$format('settings_transfer_unit_modal_title', { default: 'Transfer Home' })}
	removeScrollPadding
>
	<Container slot="content" padding="scrollPaddings">
		<Text class="mb-m">
			{$format('settings_transfer_unit_modal_info', {
				default: 'Select the profile to transfer "{name}" to.',
				values: { name: unitToTransfer?.name ?? '' },
			})}
		</Text>

		{#if otherProfiles.length > 0}
			<div class="d-flex flex-direction-column" style="gap: var(--spacing-xs)">
				{#each otherProfiles as profile (profile.email)}
					{@const itemAvatar =
						profile.acc_avatar?.backgroundColor && !profile.acc_avatar.image
							? {
									...profile.acc_avatar,
									backgroundColor: profile.acc_avatar.backgroundColor,
									image: '',
								}
							: {
									...profile.acc_avatar,
									image: profile.acc_avatar?.image || 'fak fa-solid-shelly',
								}}
					<MenuItemList
						title={getProfileDisplayName(profile.title, profile.email ?? '')}
						subtitle={profile.email}
						avatar={itemAvatar}
						hideActionsSlot
						isSelectable
						selected={selectedTransferProfile?.email === profile.email}
						on:click={() => (selectedTransferProfile = profile)}
					/>
				{/each}
			</div>
		{:else}
			<Text class="text-center">
				{$format('settings_transfer_no_profiles', {
					default: 'No other logged-in profiles available. Log in to another account first.',
				})}
			</Text>
		{/if}
	</Container>

	<div slot="footer" class="d-flex justify-content-end">
		<ButtonV2 variant="secondary" class="mr-s" on:click={() => (showTransferModal = false)}>
			{$format('button_cancel', { default: 'Cancel' })}
		</ButtonV2>
		<ButtonV2 variant="primary" disabled={!selectedTransferProfile} on:click={handleTransferSubmit}>
			{$format('button_next', { default: 'Next' })}
		</ButtonV2>
	</div>
</Modal>

<Modal
	showModal={showTransferNewAccountConfirm}
	on:close={handleTransferNewAccountDismissed}
	title={$format('settings_transfer_new_account_title', {
		default: 'New Account Created',
	})}
>
	<svelte:fragment slot="content">
		<Text>
			{$format('settings_transfer_new_account_info', {
				default:
					'A new account has been created for "{email}" and the home "{name}" has been assigned to it.',
				values: {
					email: selectedTransferProfile?.email ?? '',
					name: unitToTransfer?.name ?? '',
				},
			})}
		</Text>
	</svelte:fragment>

	<div slot="footer" class="d-flex justify-content-end">
		<ButtonV2 variant="primary" on:click={handleTransferNewAccountDismissed}>
			{$format('button_ok', { default: 'OK' })}
		</ButtonV2>
	</div>
</Modal>

<Modal
	showModal={showTransferAccountDeletionWarning}
	on:close={() => (showTransferAccountDeletionWarning = false)}
	title={$format('settings_transfer_account_deletion_title', {
		default: 'Account Will Be Deleted',
	})}
>
	<svelte:fragment slot="content">
		<Text>
			{$format('settings_transfer_account_deletion_info', {
				default:
					'"{name}" is your only remaining home. Transferring it will leave your account with no homes and your account will be permanently deleted. Are you sure you want to proceed?',
				values: { name: unitToTransfer?.name ?? '' },
			})}
		</Text>
	</svelte:fragment>

	<div slot="footer" class="d-flex justify-content-end">
		<ButtonV2
			variant="secondary"
			class="mr-s"
			on:click={() => (showTransferAccountDeletionWarning = false)}
		>
			{$format('button_cancel', { default: 'Cancel' })}
		</ButtonV2>
		<ButtonV2 variant="danger" on:click={handleTransferAccountDeletionConfirmed}>
			{$format('button_transfer_and_delete', { default: 'Transfer & Delete Account' })}
		</ButtonV2>
	</div>
</Modal>
